/**
 * Shipping store (S6.1) — 出貨通知 + 裝櫃批次 list surfaces.
 *
 * Zustand store for the two shipping list pages split out of the codex
 * ShippingPages seam: filter state (persisted), plus the store-first list
 * echoes the mutation hooks in `useShipping.ts` keep in sync (house rule:
 * mutations update the store immediately, then clearCache — never bare
 * `refresh()` from components).
 *
 * Filter contract (house list pages):
 * - notice `search` — noticeNumber / vendorName
 * - notice `status` — draft|sent|acknowledged ('all' = none)
 * - batch `search` — batchNumber / vesselOrCarrier / 櫃號 containerNumber
 * - batch `status` — open|cut_off|departed|closed ('all' = none)
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type {
  ShipmentBatch,
  ShipmentBatchStatus,
  ShippingNotice,
  ShippingNoticeStatus,
} from '@/types/api/shipping';

export type NoticeStatusFilter = 'all' | ShippingNoticeStatus;
export type BatchStatusFilter = 'all' | ShipmentBatchStatus;

export interface NoticeFilters {
  search: string;
  status: NoticeStatusFilter;
}

export interface BatchFilters {
  search: string;
  status: BatchStatusFilter;
}

export interface ShippingState {
  // Data (list echoes — kept in sync by the useShipping table hooks/mutations)
  notices: ShippingNotice[];
  batches: ShipmentBatch[];

  // Filters
  noticeFilters: NoticeFilters;
  batchFilters: BatchFilters;

  // Actions - data
  setNotices: (notices: ShippingNotice[]) => void;
  upsertNoticeInList: (notice: ShippingNotice) => void;
  setBatches: (batches: ShipmentBatch[]) => void;
  upsertBatchInList: (batch: ShipmentBatch) => void;

  // Actions - filters
  setNoticeSearch: (search: string) => void;
  setNoticeStatus: (status: NoticeStatusFilter) => void;
  resetNoticeFilters: () => void;
  setBatchSearch: (search: string) => void;
  setBatchStatus: (status: BatchStatusFilter) => void;
  resetBatchFilters: () => void;

  // Actions - reset
  reset: () => void;
}

const initialState = {
  notices: [] as ShippingNotice[],
  batches: [] as ShipmentBatch[],
  noticeFilters: { search: '', status: 'all' as NoticeStatusFilter },
  batchFilters: { search: '', status: 'all' as BatchStatusFilter },
};

export const useShippingStore = create<ShippingState>()(
  devtools(
    persist(
      (set) => ({
        ...initialState,

        setNotices: (notices) => set({ notices }, false, 'shipping/setNotices'),

        upsertNoticeInList: (notice) =>
          set(
            (state) => {
              const exists = state.notices.some((row) => row.id === notice.id);
              return {
                notices: exists
                  ? state.notices.map((row) => (row.id === notice.id ? notice : row))
                  : [notice, ...state.notices],
              };
            },
            false,
            'shipping/upsertNoticeInList'
          ),

        setBatches: (batches) => set({ batches }, false, 'shipping/setBatches'),

        upsertBatchInList: (batch) =>
          set(
            (state) => {
              const exists = state.batches.some((row) => row.id === batch.id);
              return {
                batches: exists
                  ? state.batches.map((row) => (row.id === batch.id ? batch : row))
                  : [batch, ...state.batches],
              };
            },
            false,
            'shipping/upsertBatchInList'
          ),

        setNoticeSearch: (search) =>
          set(
            (state) => ({ noticeFilters: { ...state.noticeFilters, search } }),
            false,
            'shipping/setNoticeSearch'
          ),

        setNoticeStatus: (status) =>
          set(
            (state) => ({ noticeFilters: { ...state.noticeFilters, status } }),
            false,
            'shipping/setNoticeStatus'
          ),

        resetNoticeFilters: () =>
          set(
            { noticeFilters: { ...initialState.noticeFilters } },
            false,
            'shipping/resetNoticeFilters'
          ),

        setBatchSearch: (search) =>
          set(
            (state) => ({ batchFilters: { ...state.batchFilters, search } }),
            false,
            'shipping/setBatchSearch'
          ),

        setBatchStatus: (status) =>
          set(
            (state) => ({ batchFilters: { ...state.batchFilters, status } }),
            false,
            'shipping/setBatchStatus'
          ),

        resetBatchFilters: () =>
          set(
            { batchFilters: { ...initialState.batchFilters } },
            false,
            'shipping/resetBatchFilters'
          ),

        reset: () => set(initialState, false, 'shipping/reset'),
      }),
      {
        name: 'shipping-storage',
        partialize: (state) => ({
          noticeFilters: state.noticeFilters,
          batchFilters: state.batchFilters,
        }),
      }
    ),
    { name: 'ShippingStore' }
  )
);
